import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Loader2, CheckCircle2, Upload, X, Lock, ShieldCheck,
  ArrowRight, ArrowLeft, User, MapPin, Camera,
  BadgeCheck, Mail, Hash,
} from 'lucide-react';
import { toast } from 'sonner';
import { agentUpdateAPI } from '@/lib/api';
import { US_STATES, formatPhoneInput } from '@/lib/utils';
import AddressAutocomplete from '@/components/common/AddressAutocomplete';

const NAVY = '#0b1e39';
const GOLD = '#d4a244';

const STEPS = [
  { id: 'identity', title: 'Your Identity', icon: User },
  { id: 'contact', title: 'Address & SSN', icon: MapPin },
  { id: 'profile', title: 'Profile', icon: Camera },
  { id: 'review', title: 'Review & Save', icon: BadgeCheck },
];

/* shells at module scope so inputs keep focus across re-renders */
const Page = ({ children }) => (
  <div className="min-h-screen w-full relative flex items-center justify-center p-4 sm:p-6 overflow-hidden" style={{ background: '#f6f7fa' }}>
    <div
      aria-hidden="true"
      className="pointer-events-none absolute inset-0"
      style={{
        backgroundImage: 'url(/brand/kr-logo.webp)',
        backgroundRepeat: 'repeat',
        backgroundSize: '300px auto',
        backgroundPosition: 'center top',
        opacity: 0.13,
      }}
    />
    <div className="w-full max-w-2xl relative z-10">{children}</div>
  </div>
);

const Header = ({ compact }) => (
  <div
    className="flex items-center justify-between px-6 sm:px-9 py-6 rounded-t-2xl"
    style={{ background: `linear-gradient(135deg, ${NAVY}, #14315c)`, borderBottom: `3px solid ${GOLD}` }}
  >
    <div>
      <div className="text-[11px] font-bold tracking-[0.2em] uppercase" style={{ color: GOLD }}>KR Enterprise Group</div>
      <div className="text-white text-lg font-semibold mt-0.5">Update Your Information</div>
    </div>
    {!compact && (
      <div className="hidden sm:flex items-center gap-1.5 rounded-full px-3 py-1" style={{ background: 'rgba(212,162,68,0.14)', border: `1px solid ${GOLD}55` }}>
        <ShieldCheck size={13} style={{ color: GOLD }} />
        <span className="text-[11px] font-semibold" style={{ color: GOLD }}>256-bit Secure</span>
      </div>
    )}
  </div>
);

const Card = ({ children }) => (
  <div className="bg-white rounded-2xl overflow-hidden" style={{ boxShadow: '0 24px 60px rgba(11,30,57,0.10)' }}>
    {children}
  </div>
);

const ReadOnlyCard = ({ icon: Icon, label, value }) => (
  <div className="flex items-center gap-2.5 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
    <Icon size={14} className="text-slate-400 shrink-0" />
    <div className="min-w-0">
      <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">{label}</div>
      <div className="text-sm font-medium text-slate-800 truncate">{value}</div>
    </div>
    <Lock size={11} className="text-slate-300 ml-auto shrink-0" />
  </div>
);

export default function AgentInfoUpdatePage() {
  const { token } = useParams();
  const fileRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [linkError, setLinkError] = useState(null);
  const [info, setInfo] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [step, setStep] = useState(0);
  const [dir, setDir] = useState(1);

  const [form, setForm] = useState({
    first_name: '', last_name: '', phone: '', date_of_birth: '',
    address_line1: '', address_line2: '', city: '', state: '', zip: '',
    preferred_language: 'en', ssn_full: '', photo_base64: null,
  });
  const setField = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  useEffect(() => {
    agentUpdateAPI.getPublic(token)
      .then((r) => {
        const d = r.data || {};
        setInfo(d);
        setForm((p) => ({
          ...p,
          first_name: d.first_name || '',
          last_name: d.last_name || '',
          phone: d.phone || '',
          date_of_birth: d.date_of_birth || '',
          address_line1: d.address_line1 || '',
          address_line2: d.address_line2 || '',
          city: d.city || '',
          state: d.state || '',
          zip: d.zip || '',
          preferred_language: d.preferred_language || 'en',
        }));
      })
      .catch((e) => setLinkError(e?.response?.data?.detail || 'This link is invalid or has expired.'))
      .finally(() => setLoading(false));
  }, [token]);

  const ssnOnFile = useMemo(() => (info?.ssn_last4 ? `•••-••-${info.ssn_last4}` : ''), [info]);

  const handlePhoto = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error('Image must be 10 MB or less'); return; }
    const reader = new FileReader();
    reader.onload = () => { setForm((p) => ({ ...p, photo_base64: reader.result })); setPhotoPreview(reader.result); };
    reader.readAsDataURL(file);
  };
  const clearPhoto = () => {
    setForm((p) => ({ ...p, photo_base64: null }));
    setPhotoPreview(null);
    if (fileRef.current) fileRef.current.value = '';
  };

  const validateStep = (s) => {
    if (s === 0) {
      if (!form.first_name.trim() || !form.last_name.trim()) return 'Your full legal name is required';
      if (!form.phone.trim()) return 'Phone number is required';
      if (!/^\d{4}-\d{2}-\d{2}$/.test(form.date_of_birth)) return 'Date of birth is required';
    }
    if (s === 1) {
      const ssn = form.ssn_full.replace(/\D/g, '');
      if (ssn && ssn.length !== 9) return 'Enter a full 9-digit SSN, or leave it blank to keep the one on file';
      if (!form.address_line1.trim() || !form.city.trim() || !form.state || !form.zip.trim()) return 'A complete address is required';
    }
    return null;
  };

  const next = () => {
    const err = validateStep(step);
    if (err) { toast.error(err); return; }
    setDir(1); setStep((s) => Math.min(s + 1, STEPS.length - 1));
  };
  const back = () => { setDir(-1); setStep((s) => Math.max(s - 1, 0)); };

  const handleSubmit = async () => {
    for (let s = 0; s <= 1; s += 1) {
      const err = validateStep(s);
      if (err) { toast.error(err); setDir(-1); setStep(s); return; }
    }
    setSubmitting(true);
    try {
      const payload = { ...form };
      // Blank SSN → don't send it (keeps existing encrypted value).
      if (!payload.ssn_full.replace(/\D/g, '')) delete payload.ssn_full;
      await agentUpdateAPI.submitPublic(token, payload);
      setDone(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not save your information. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <Page><Card><Header compact /><div className="flex items-center justify-center py-16 text-slate-500"><Loader2 className="animate-spin mr-2" /> Loading your details…</div></Card></Page>;
  }

  if (linkError) {
    return (
      <Page><Card><Header compact />
        <div className="text-center px-6 py-14" data-testid="update-link-error">
          <div className="mx-auto mb-4 h-16 w-16 rounded-full flex items-center justify-center" style={{ background: '#fdf6e3' }}>
            <ShieldCheck className="text-amber-500" size={30} />
          </div>
          <h2 className="text-xl font-semibold" style={{ color: NAVY }}>Link unavailable</h2>
          <p className="text-sm text-slate-500 mt-2 max-w-sm mx-auto">{linkError}</p>
          <p className="text-xs text-slate-400 mt-5">Please contact your agency to request a new link.</p>
        </div>
      </Card></Page>
    );
  }

  if (done) {
    return (
      <Page><Card><Header compact />
        <div className="text-center px-6 py-14" data-testid="update-success">
          <motion.div
            initial={{ scale: 0, rotate: -20 }} animate={{ scale: 1, rotate: 0 }}
            transition={{ type: 'spring', stiffness: 200, damping: 12 }}
            className="mx-auto mb-5 h-20 w-20 rounded-full flex items-center justify-center relative"
            style={{ background: NAVY }}
          >
            <span className="absolute inset-0 rounded-full animate-ping" style={{ background: `${GOLD}40` }} />
            <CheckCircle2 className="relative" size={40} style={{ color: GOLD }} />
          </motion.div>
          <h2 className="text-2xl font-semibold" style={{ color: NAVY }}>Thank you, {form.first_name}!</h2>
          <p className="text-sm text-slate-600 mt-2 max-w-md mx-auto">
            Your information has been updated and sent securely to {info?.agency_name || 'your agency'}.
            You can safely close this window.
          </p>
        </div>
      </Card></Page>
    );
  }

  const CurrentIcon = STEPS[step].icon;

  return (
    <Page>
      <Card>
        <Header />

        {/* Known identity cards */}
        <div className="px-6 sm:px-9 pt-6 grid grid-cols-1 sm:grid-cols-3 gap-2.5">
          <ReadOnlyCard icon={BadgeCheck} label="Agency" value={info?.agency_name || '—'} />
          <ReadOnlyCard icon={Hash} label="NPN" value={info?.npn || '—'} />
          <ReadOnlyCard icon={Mail} label="Email" value={info?.email || '—'} />
        </div>

        {/* Stepper */}
        <div className="px-6 sm:px-9 pt-6" data-testid="update-stepper">
          <div className="flex items-center">
            {STEPS.map((st, i) => {
              const StIcon = st.icon;
              const active = i === step;
              const complete = i < step;
              return (
                <React.Fragment key={st.id}>
                  <div className="flex flex-col items-center">
                    <div
                      className="h-9 w-9 rounded-full flex items-center justify-center transition-all"
                      style={{
                        background: complete ? '#15794a' : active ? NAVY : '#eef1f6',
                        boxShadow: active ? `0 0 0 4px ${GOLD}33` : 'none',
                        color: complete || active ? '#fff' : '#9aa6b8',
                      }}
                    >
                      {complete ? <CheckCircle2 size={16} /> : <StIcon size={15} />}
                    </div>
                    <span className="hidden sm:block text-[10px] mt-1.5 font-medium" style={{ color: active ? NAVY : '#9aa6b8' }}>{st.title}</span>
                  </div>
                  {i < STEPS.length - 1 && (
                    <div className="flex-1 h-0.5 mx-1.5 rounded" style={{ background: i < step ? '#15794a' : '#e5e9f0' }} />
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>

        {/* Step body */}
        <div className="px-6 sm:px-9 py-7 min-h-[320px]">
          <div className="flex items-center gap-2 mb-5">
            <CurrentIcon size={18} style={{ color: GOLD }} />
            <h3 className="text-lg font-semibold" style={{ color: NAVY }}>{STEPS[step].title}</h3>
          </div>

          <AnimatePresence mode="wait" custom={dir}>
            <motion.div
              key={step}
              custom={dir}
              initial={{ opacity: 0, x: dir * 28 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: dir * -28 }}
              transition={{ duration: 0.25 }}
              className="space-y-4"
            >
              {step === 0 && (
                <>
                  <p className="text-sm text-slate-500">Your details are pre-filled — please confirm or correct them.</p>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label htmlFor="fn">First name (as on license) *</Label>
                      <Input id="fn" value={form.first_name} onChange={(e) => setField('first_name', e.target.value)} data-testid="up-first-name" />
                    </div>
                    <div>
                      <Label htmlFor="ln">Last name (as on license) *</Label>
                      <Input id="ln" value={form.last_name} onChange={(e) => setField('last_name', e.target.value)} data-testid="up-last-name" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label htmlFor="ph">Phone *</Label>
                      <Input id="ph" value={formatPhoneInput(form.phone)} onChange={(e) => setField('phone', e.target.value)} placeholder="(863) 555-1234" data-testid="up-phone" />
                    </div>
                    <div>
                      <Label htmlFor="dob">Date of birth *</Label>
                      <Input id="dob" type="date" value={form.date_of_birth} onChange={(e) => setField('date_of_birth', e.target.value)} data-testid="up-dob" />
                    </div>
                  </div>
                </>
              )}

              {step === 1 && (
                <>
                  <div className="flex items-center gap-2 rounded-lg px-3 py-2 mb-1" style={{ background: '#f6fbf7', border: '1px solid #cbe8d6' }}>
                    <ShieldCheck size={15} style={{ color: '#15794a' }} />
                    <span className="text-xs font-medium" style={{ color: '#15794a' }}>Your SSN is encrypted with 256-bit security and never shared.</span>
                  </div>
                  <div>
                    <Label htmlFor="ssn">Social Security Number {info?.has_full_ssn ? '' : '*'}</Label>
                    <Input
                      id="ssn"
                      value={form.ssn_full}
                      onChange={(e) => setField('ssn_full', e.target.value)}
                      placeholder={info?.has_full_ssn ? `On file: ${ssnOnFile} — leave blank to keep` : 'XXX-XX-XXXX'}
                      inputMode="numeric"
                      data-testid="up-ssn"
                      style={{ fontFamily: 'JetBrains Mono, monospace' }}
                    />
                    {info?.has_full_ssn && (
                      <p className="text-[11px] text-slate-400 mt-1">We already have an SSN on file ending in {info.ssn_last4}. Only type a new one if it needs to change.</p>
                    )}
                  </div>
                  <div>
                    <Label htmlFor="a1">Street address *</Label>
                    <AddressAutocomplete
                      value={form.address_line1}
                      onChange={(v) => setField('address_line1', v)}
                      onPlaceSelected={(p) => setForm((prev) => ({
                        ...prev,
                        address_line1: p.street || prev.address_line1,
                        city: p.city || prev.city,
                        state: p.state || prev.state,
                        zip: p.zip || prev.zip,
                      }))}
                      placeholder="Start typing your address…"
                      testId="up-address"
                    />
                  </div>
                  <div>
                    <Label htmlFor="a2">Apt / Suite (optional)</Label>
                    <Input id="a2" value={form.address_line2} onChange={(e) => setField('address_line2', e.target.value)} data-testid="up-address2" />
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div className="col-span-1">
                      <Label htmlFor="city">City *</Label>
                      <Input id="city" value={form.city} onChange={(e) => setField('city', e.target.value)} data-testid="up-city" />
                    </div>
                    <div>
                      <Label>State *</Label>
                      <Select value={form.state} onValueChange={(v) => setField('state', v)}>
                        <SelectTrigger data-testid="up-state"><SelectValue placeholder="—" /></SelectTrigger>
                        <SelectContent>
                          {US_STATES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="zip">ZIP *</Label>
                      <Input id="zip" value={form.zip} onChange={(e) => setField('zip', e.target.value)} data-testid="up-zip" />
                    </div>
                  </div>
                </>
              )}

              {step === 2 && (
                <>
                  <div>
                    <Label>Profile photo (optional)</Label>
                    <div className="flex items-center gap-4 mt-1.5">
                      {photoPreview ? (
                        <div className="relative">
                          <img src={photoPreview} alt="preview" className="w-20 h-20 rounded-full object-cover border-2" style={{ borderColor: GOLD }} />
                          <button type="button" onClick={clearPhoto} className="absolute -top-1 -right-1 bg-slate-800 text-white rounded-full p-0.5" data-testid="up-photo-clear"><X size={12} /></button>
                        </div>
                      ) : (
                        <div className="w-20 h-20 rounded-full bg-slate-100 border flex items-center justify-center text-slate-300"><Upload size={20} /></div>
                      )}
                      <input ref={fileRef} type="file" accept="image/*" onChange={handlePhoto} className="text-sm" data-testid="up-photo-input" />
                    </div>
                    {info?.has_photo && !photoPreview && (
                      <p className="text-[11px] text-slate-400 mt-1.5">You already have a photo on file. Upload a new one only if you'd like to replace it.</p>
                    )}
                  </div>
                  <div>
                    <Label>Preferred language</Label>
                    <Select value={form.preferred_language} onValueChange={(v) => setField('preferred_language', v)}>
                      <SelectTrigger data-testid="up-language"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="en">English</SelectItem>
                        <SelectItem value="es">Español</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </>
              )}

              {step === 3 && (
                <div className="space-y-3" data-testid="up-review">
                  <div className="rounded-xl border border-slate-200 divide-y divide-slate-100">
                    {[
                      ['Name', `${form.first_name} ${form.last_name}`],
                      ['Phone', form.phone],
                      ['Date of birth', form.date_of_birth],
                      ['SSN', form.ssn_full.replace(/\D/g, '')
                        ? `•••-••-${form.ssn_full.replace(/\D/g, '').slice(-4)} (updated)`
                        : (ssnOnFile ? `${ssnOnFile} (unchanged)` : 'Not provided')],
                      ['Address', [form.address_line1, form.address_line2, `${form.city}, ${form.state} ${form.zip}`].filter(Boolean).join(', ')],
                    ].map(([k, v]) => (
                      <div key={k} className="flex justify-between gap-4 px-4 py-2.5 text-sm">
                        <span className="text-slate-400">{k}</span>
                        <span className="font-medium text-slate-800 text-right truncate">{v}</span>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-slate-400">By submitting, you confirm this information is accurate and up to date.</p>
                </div>
              )}
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Footer nav */}
        <div className="px-6 sm:px-9 py-5 border-t border-slate-100 flex items-center justify-between">
          <Button variant="ghost" onClick={back} disabled={step === 0 || submitting} data-testid="up-back" className="text-slate-500">
            <ArrowLeft size={15} className="mr-1" /> Back
          </Button>
          {step < STEPS.length - 1 ? (
            <Button onClick={next} className="text-white" style={{ background: NAVY }} data-testid="up-next">
              Continue <ArrowRight size={15} className="ml-1.5" />
            </Button>
          ) : (
            <Button onClick={handleSubmit} disabled={submitting} className="text-white" style={{ background: '#15794a' }} data-testid="up-submit">
              {submitting ? <Loader2 size={16} className="mr-2 animate-spin" /> : <BadgeCheck size={16} className="mr-1.5" />}
              Save My Information
            </Button>
          )}
        </div>
      </Card>
      <p className="text-center text-[11px] text-slate-400 mt-4">© KR Enterprise Group · Confidential &amp; secure</p>
    </Page>
  );
}
